import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Mail, Send, RefreshCcw, CheckCircle2, AlertCircle, ShieldAlert,
  Hourglass, Loader2, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, isSuperAdmin } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Kandake Email Configuration & Delivery Inspector — Super-Admin only.
//
// Shows only configured / not-configured booleans + a redacted hint per
// address. NEVER displays the API key or the full email values directly;
// the backend redacts them deliberately. Provides a one-shot test message
// control and a paginated delivery log with retry actions.
// ---------------------------------------------------------------------------

const TEMPLATE_LABELS = {
  "consultation.confirmation": "Consultation — Requester",
  "consultation.internal":     "Consultation — Internal Notification",
  "demo.public.confirmation":  "Demo (Public) — Submitter",
  "demo.public.internal":      "Demo (Public) — Internal Notification",
  "demo.internal.notify":      "Demo (Internal Intake) — Notification",
  "demo.doc_request":          "Demo — Document Request",
  "admin.test":                "Admin — Test Email",
};

const STATUS_COLOUR = {
  sent: {
    bg: "hsl(135 35% 90%)", fg: "hsl(135 35% 22%)", icon: CheckCircle2,
    label: "Sent",
  },
  queued: {
    bg: "hsl(40 60% 92%)", fg: "hsl(40 60% 24%)", icon: Hourglass,
    label: "Queued",
  },
  failed: {
    bg: "hsl(0 50% 92%)", fg: "hsl(0 50% 26%)", icon: AlertCircle,
    label: "Failed",
  },
  not_configured: {
    bg: "hsl(225 14% 92%)", fg: "hsl(225 14% 28%)", icon: ShieldAlert,
    label: "Not configured",
  },
};

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function StatusBadge({ status, testid }) {
  const s = STATUS_COLOUR[status] || STATUS_COLOUR.not_configured;
  const Icon = s.icon;
  return (
    <Badge
      variant="outline"
      style={{ background: s.bg, color: s.fg, borderColor: `${s.fg}33` }}
      className="text-[10px] uppercase tracking-wide font-medium whitespace-nowrap"
      data-testid={testid}
    >
      <Icon className="w-3 h-3 mr-1" /> {s.label}
    </Badge>
  );
}

function ConfigPill({ label, ok, hint, testid }) {
  return (
    <div className="rounded-md border p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        {ok ? (
          <CheckCircle2 className="w-4 h-4" style={{ color: "hsl(135 35% 30%)" }} />
        ) : (
          <AlertCircle className="w-4 h-4" style={{ color: "hsl(0 50% 36%)" }} />
        )}
        <span className="text-[12.5px] font-medium" data-testid={`${testid}-state`}>
          {ok ? "Configured" : "Not configured"}
        </span>
      </div>
      {hint && (
        <div className="text-[11px] text-muted-foreground mt-1 truncate" title={hint} data-testid={`${testid}-hint`}>
          {hint}
        </div>
      )}
    </div>
  );
}

export default function AdminEmail() {
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTemplate, setFilterTemplate] = useState("all");

  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!superAdmin) return;
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/email/status"),
        api.get("/admin/email/deliveries", {
          params: {
            ...(filterStatus !== "all" ? { status: filterStatus } : {}),
            ...(filterTemplate !== "all" ? { template_type: filterTemplate } : {}),
            limit: 100,
          },
        }),
      ]);
      setStatus(s.data || null);
      setDeliveries(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load email configuration."));
    } finally {
      setLoading(false);
    }
  }, [superAdmin, filterStatus, filterTemplate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function sendTest() {
    const to = (testTo || "").trim();
    if (!to) {
      toast.error("Enter a recipient address.");
      return;
    }
    setTestBusy(true);
    try {
      const r = await api.post("/admin/email/test", { to });
      const st = r.data?.status || "unknown";
      if (st === "sent") toast.success("Test email sent.");
      else if (st === "not_configured") toast.warning("Test logged but Resend is not configured.");
      else toast.error(`Test failed (${st}).`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Could not send test email."));
    } finally {
      setTestBusy(false);
    }
  }

  async function retry(id) {
    try {
      const r = await api.post(`/admin/email/deliveries/${id}/retry`);
      const st = r.data?.status || "unknown";
      if (st === "sent") toast.success("Resend succeeded.");
      else if (st === "not_configured") toast.warning("Still not configured.");
      else toast.error(`Retry result: ${st}.`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Retry failed."));
    }
  }

  const filteredDeliveries = useMemo(() => deliveries, [deliveries]);

  if (!superAdmin) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="admin-email-readonly">
        <Alert>
          <AlertDescription>
            The Email configuration panel is visible to Super Admin only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Kandake Operations · Outbound Email
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
            <Mail className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Email Delivery
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Configuration status, test message control, and full audit of every outbound message
            Kandake has attempted. Provider keys are never displayed in the interface.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="admin-email-refresh">
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuration status</CardTitle>
          <CardDescription>
            Values are read from server environment variables. Only configured / not-configured booleans
            and a redacted hint per address are surfaced.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4" data-testid="admin-email-status">
          {loading || !status ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading\u2026
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ConfigPill
                  label="Resend API key"
                  ok={!!status.api_key_configured}
                  hint={status.api_key_configured ? "Key present (redacted)" : "RESEND_API_KEY"}
                  testid="cfg-api-key"
                />
                <ConfigPill
                  label="From address"
                  ok={!!status.from_email_configured}
                  hint={status.from_email_hint || "RESEND_FROM_EMAIL"}
                  testid="cfg-from"
                />
                <ConfigPill
                  label="Reply-To"
                  ok={!!status.reply_to_configured}
                  hint={status.reply_to_hint || "RESEND_REPLY_TO_EMAIL"}
                  testid="cfg-reply-to"
                />
                <ConfigPill
                  label="Internal notification"
                  ok={!!status.internal_recipient_configured}
                  hint={status.internal_recipient_hint || "KANDAKE_INTERNAL_NOTIFICATION_EMAIL"}
                  testid="cfg-internal"
                />
              </div>
              {!status.fully_configured && (
                <Alert>
                  <AlertDescription className="text-[12.5px]">
                    Outbound delivery is currently disabled because at least one required value is
                    missing. Public submissions still succeed and are recorded; pending emails are
                    logged below and can be retried after configuration is corrected.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Send test email</CardTitle>
          <CardDescription>
            Sends a one-off message through the configured Resend sender. The attempt — whether it
            succeeds or fails — is logged in the delivery table below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="admin-email-test">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-[11px] uppercase tracking-wide">Recipient address</Label>
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="qa@example.com"
                data-testid="admin-email-test-to"
              />
            </div>
            <Button onClick={sendTest} disabled={testBusy} data-testid="admin-email-test-send">
              {testBusy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send test
            </Button>
          </div>
          {status?.last_test && (
            <div
              className="rounded-md border p-3 text-[12.5px] flex flex-wrap items-center gap-3"
              data-testid="admin-email-test-last"
            >
              <span className="text-muted-foreground">Last test:</span>
              <StatusBadge status={status.last_test.status} testid="admin-email-test-last-status" />
              <span>{status.last_test.recipient || "—"}</span>
              <span className="text-muted-foreground">{fmtDate(status.last_test.created_at)}</span>
              {status.last_test.provider_id && (
                <span className="font-mono text-[11px] text-muted-foreground">
                  {status.last_test.provider_id}
                </span>
              )}
              {status.last_test.error_message && (
                <span className="text-[11.5px]" style={{ color: "hsl(0 50% 36%)" }}>
                  {status.last_test.error_message}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Inbox className="w-4 h-4 opacity-70" /> Delivery log
            </CardTitle>
            <CardDescription>Most recent 100 outbound attempts.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]" data-testid="admin-email-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="not_configured">Not configured</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTemplate} onValueChange={setFilterTemplate}>
              <SelectTrigger className="w-[260px]" data-testid="admin-email-filter-template">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All templates</SelectItem>
                {Object.entries(TEMPLATE_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="admin-email-deliveries">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Created</TableHead>
                <TableHead className="w-[160px]">Status</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="w-[90px]">Attempts</TableHead>
                <TableHead className="w-[110px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading\u2026
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredDeliveries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-10" data-testid="admin-email-empty">
                    No deliveries match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredDeliveries.map((d) => (
                <TableRow key={d.id} data-testid={`admin-email-row-${d.id}`}>
                  <TableCell className="text-[12px]">{fmtDate(d.created_at)}</TableCell>
                  <TableCell>
                    <StatusBadge status={d.status} testid={`admin-email-status-${d.id}`} />
                    {d.error_message && (
                      <div className="text-[11px] mt-1" style={{ color: "hsl(0 50% 36%)" }}
                        title={d.error_message} data-testid={`admin-email-error-${d.id}`}
                      >
                        {d.error_message.length > 60
                          ? d.error_message.slice(0, 60) + "\u2026"
                          : d.error_message}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-[12.5px]">
                    {TEMPLATE_LABELS[d.template_type] || d.template_type}
                  </TableCell>
                  <TableCell className="text-[12.5px]">{d.recipient || "—"}</TableCell>
                  <TableCell className="text-[12.5px] max-w-[260px] truncate" title={d.subject}>
                    {d.subject || "—"}
                  </TableCell>
                  <TableCell className="text-[12.5px]">{d.attempts_total ?? d.attempt ?? 1}</TableCell>
                  <TableCell className="text-right">
                    {(d.status === "failed" || d.status === "not_configured") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => retry(d.id)}
                        data-testid={`admin-email-retry-${d.id}`}
                      >
                        <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Retry
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
